import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { PROJECTS } from '../../data/site';
import useParallax from '../../hooks/useParallax';
import Container from '../ui/Container';
import Chip from '../ui/Chip';
import Reveal from '../ui/Reveal';

function ProjectRow({ project, reversed }) {
  const { ref, y } = useParallax(28);

  return (
    <article className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
      <Reveal from={reversed ? 'right' : 'left'} className={`lg:col-span-7 ${reversed ? 'lg:order-2' : ''}`}>
        <motion.a
          ref={ref}
          style={{ y }}
          href={project.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${project.name} 사이트 열기`}
          className="group block rounded-3xl bg-white/10 p-2 backdrop-blur-sm"
        >
          <div className="overflow-hidden rounded-2xl">
            <img
              src={project.thumb}
              alt={project.alt}
              width={900}
              height={562}
              loading="lazy"
              className="w-full transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            />
          </div>
        </motion.a>
      </Reveal>

      <Reveal from={reversed ? 'left' : 'right'} delay={0.1} className={`lg:col-span-5 ${reversed ? 'lg:order-1' : ''}`}>
        <p className="text-[14px] font-semibold text-sky">{project.type}</p>
        <h3 className="mt-2 text-[1.75rem] font-bold leading-[1.2] tracking-[-0.02em] sm:text-4xl">{project.name}</h3>
        <p className="mt-4 text-[15px] leading-[1.8] text-white/70 sm:text-base">{project.summary}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {project.highlights.map((item) => (
            <Chip key={item} tone="inverse">
              {item}
            </Chip>
          ))}
        </div>
        <a
          href={project.url}
          target="_blank"
          rel="noreferrer"
          className="group mt-8 inline-flex items-center gap-2 text-[15px] font-semibold text-white"
        >
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-night transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
            <ArrowUpRight size={18} weight="bold" />
          </span>
          {project.domain} 열기
        </a>
      </Reveal>
    </article>
  );
}

function ProjectShowcase() {
  return (
    <section className="relative overflow-hidden bg-night py-20 text-white sm:py-28">
      <div aria-hidden="true" className="bg-night-glow pointer-events-none absolute inset-0" />
      <Container className="relative">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <Reveal from="left" className="max-w-2xl">
            <h2 className="text-balance text-[1.85rem] font-bold leading-[1.2] tracking-[-0.025em] sm:text-4xl lg:text-[2.75rem]">
              지금 운영 중인 사이트입니다
            </h2>
            <p className="mt-4 text-base leading-[1.75] text-white/70 sm:text-lg">
              두 프로젝트 모두 기획부터 배포까지 PageN이 진행했습니다. 화면을 누르면 새 창에서 바로 열립니다.
            </p>
          </Reveal>
          <Reveal from="right" delay={0.1}>
            <Link
              to="/projects"
              className="group inline-flex shrink-0 items-center gap-1.5 text-[15px] font-semibold text-white/85 transition-colors hover:text-white"
            >
              프로젝트 보기
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 flex flex-col gap-20 sm:gap-28">
          {PROJECTS.map((project, index) => (
            <ProjectRow key={project.slug} project={project} reversed={index % 2 === 1} />
          ))}
        </div>
      </Container>
    </section>
  );
}

export default ProjectShowcase;
